import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Link,
  Alert,
  Snackbar,
  useMediaQuery,
} from '@mui/material';
import {
  LocationOn,
  SquareFoot,
  Bathtub,
  KingBed,
  Edit,
  Share,
  Favorite,
  FavoriteBorder,
  NavigateBefore,
  NavigateNext,
  Phone,
  Email,
  Photo,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import photosPlaceholderSrc from '../assets/PhotosPlaceholder.png';
import { formatPropertyAddress, propertyImageAlt } from '../utils/propertyDisplay';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [property, setProperty] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [shareToast, setShareToast] = useState({ open: false, message: '' });

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch property details
        const propertyResponse = await api.properties.getById(id);
        const propertyData = propertyResponse.data;
        setProperty(propertyData);

        // Increment views once per session per property (skip when vendor is viewing their own listing)
        try {
          const viewerUid = userProfile?.uid || null;
          const isOwnerViewing = Boolean(viewerUid && propertyData?.vendorId && viewerUid === propertyData.vendorId);
          const sessionKey = `offmarkets:viewed_property:${id}`;
          const alreadyCounted = typeof window !== 'undefined' && window.sessionStorage?.getItem(sessionKey) === '1';

          if (!isOwnerViewing && !alreadyCounted) {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.setItem(sessionKey, '1');
            }
            // Fire-and-forget; UI doesn't block on this.
            api.properties.incrementViews(id).catch(() => {});
          }
        } catch (e) {
          // Do not break the page if sessionStorage is unavailable.
        }

        // Fetch vendor information
        if (propertyData.vendorId) {
          const vendorResponse = await api.vendors.getById(propertyData.vendorId);
          setVendor(vendorResponse.data);
        }

        // Check if property is saved by this investor
        if (userProfile?.uid) {
          const savedResponse = await api.properties.isSaved(id, userProfile.uid);
          setIsFavorite(Boolean(savedResponse?.data?.saved));
        } else {
          setIsFavorite(false);
        }

      } catch (err) {
        console.error('Error fetching property data:', err);
        setError('Failed to load property details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPropertyData();
    }
  }, [id, userProfile?.uid]);

  const handleEditProperty = () => {
    // Navigate back to vendor dashboard or open edit modal
    // For now, we'll navigate back to the vendor dashboard
    navigate('/vendor');
  };

  const handleToggleFavorite = () => {
    (async () => {
      try {
        const result = await api.properties.toggleSaved(id, {
          investorName: userProfile?.displayName || userProfile?.email || null
        });
        setIsFavorite(Boolean(result?.data?.saved));
      } catch (err) {
        console.error('Failed to toggle saved property:', err);
      }
    })();
  };

  const handleShareProperty = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = property ? formatPropertyAddress(property, 'OffMarkets property') : 'OffMarkets property';
    const text = title;

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({ title, text, url });
        return;
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareToast({ open: true, message: 'Link copied to clipboard' });
        return;
      }

      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setShareToast({ open: true, message: 'Link copied to clipboard' });
    } catch (err) {
      console.error('Share failed:', err);
      setShareToast({ open: true, message: 'Could not share. Please copy the URL from the address bar.' });
    }
  };

  const handleNextImage = () => {
    if (property?.images && property.images.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (property?.images && property.images.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? property.images.length - 1 : prev - 1
      );
    }
  };

  const formatPrice = (price) => {
    const n = typeof price === 'string' ? Number(price) : price;
    if (!Number.isFinite(n)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = dateString?.seconds
      ? new Date(dateString.seconds * 1000)
      : (dateString instanceof Date ? dateString : new Date(dateString));
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const PLACEHOLDER_PHOTO_SRC = photosPlaceholderSrc;

  const normalizeUrl = (url) => {
    const u = (url ?? '').toString().trim();
    if (!u) return '';
    return /^https?:\/\//i.test(u) ? u : `https://${u}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#008080' }} size={60} />
      </Box>
    );
  }

  if (error || !property) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
        <Container maxWidth="md">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Property not found'}
          </Alert>
        </Container>
      </Box>
    );
  }

  const images = Array.from(
    new Set([...(property.images || []), ...(property.externalImageUrls || [])].filter(Boolean))
  );
  const hasImages = images.length > 0;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Property Address and Actions */}
            <Box sx={{ mb: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                spacing={2}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.9rem', md: '2.125rem' } }}>
                    {formatPropertyAddress(property)}
                  </Typography>
                  {property.propertyType && (
                    <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
                  >
                      <LocationOn sx={{ mr: 1, fontSize: 20 }} />
                      {property.propertyType}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <IconButton onClick={handleToggleFavorite} sx={{ color: isFavorite ? '#FF6B6B' : 'grey.400' }}>
                    {isFavorite ? <Favorite /> : <FavoriteBorder />}
                  </IconButton>
                  <IconButton onClick={handleShareProperty} sx={{ color: 'grey.600' }}>
                    <Share />
                  </IconButton>
                  {userProfile?.userType === 'vendor' && (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={handleEditProperty}
                      sx={{ borderColor: '#008080', color: '#008080' }}
                      fullWidth={isMobile}
                    >
                      Edit Property
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>

            {/* Image Gallery */}
            <Card sx={{ mb: 4 }}>
              <Box sx={{ position: 'relative', height: { xs: 240, sm: 320, md: 400 }, overflow: 'hidden' }}>
                {hasImages ? (
                  <>
                    <Box
                      component="img"
                      src={images[currentImageIndex]}
                      alt={`${propertyImageAlt(property)} ${currentImageIndex + 1}`}
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img?.src && img.src.includes('PhotosPlaceholder.png')) return;
                        img.src = PLACEHOLDER_PHOTO_SRC;
                      }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {images.length > 1 && (
                      <>
                        <IconButton
                          onClick={handlePrevImage}
                          sx={{
                            position: 'absolute',
                            left: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                          }}
                        >
                          <NavigateBefore />
                        </IconButton>
                        <IconButton
                          onClick={handleNextImage}
                          sx={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                          }}
                        >
                          <NavigateNext />
                        </IconButton>
                        <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}>
                          <Stack direction="row" spacing={1}>
                            {images.map((_, index) => (
                              <Box
                                key={index}
                                onClick={() => setCurrentImageIndex(index)}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                  cursor: 'pointer',
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Box
                    component="img"
                    src={PLACEHOLDER_PHOTO_SRC}
                    alt="Photos coming soon"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: 'white'
                    }}
                  />
                )}
              </Box>
            </Card>

            {/* Property Details */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <KingBed sx={{ fontSize: 24, color: '#008080', mb: 1 }} />
                  <Typography variant="h6">{property.bedrooms || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Bedrooms</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Bathtub sx={{ fontSize: 24, color: '#008080', mb: 1 }} />
                  <Typography variant="h6">{property.bathrooms || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <SquareFoot sx={{ fontSize: 24, color: '#008080', mb: 1 }} />
                  <Typography variant="h6">{property.squareFootage ? `${property.squareFootage.toLocaleString()} sqft` : 'N/A'}</Typography>
                  <Typography variant="body2" color="text.secondary">Square Feet</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <SquareFoot sx={{ fontSize: 24, color: '#008080', mb: 1 }} />
                  <Typography variant="h6">
                    {property.lotSize ? `${property.lotSize} ${property.lotSizeUnit || 'acres'}` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Lot Size</Typography>
                </Card>
              </Grid>
              {/* Year Built removed */}
            </Grid>

            {/* Property Description */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {property.description || 'No description available.'}
                </Typography>
              </CardContent>
            </Card>

            {/* Property Features */}
            {property.features && property.features.length > 0 && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>Features</Typography>
                  <Stack direction="row" flexWrap="wrap" spacing={1}>
                    {property.features.map((feature, index) => (
                      <Chip key={index} label={feature} variant="outlined" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Asking Price */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#008080' }}>
                  {formatPrice(property.price)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Asking Price
                </Typography>
              </CardContent>
            </Card>

            {/* Close of Escrow */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#008080' }}>
                  {formatDate(property.closeOfEscrow)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Close of Escrow
                </Typography>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Details
                </Typography>
                <Divider sx={{ my: 2 }} />
                {property.hoaFee && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>HOA Fee:</strong> {formatPrice(property.hoaFee)}/month
                  </Typography>
                )}
                {property.spaceRent && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Space Rent:</strong> {formatPrice(property.spaceRent)}/sq ft/year
                  </Typography>
                )}
                {/* Zoning removed */}
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={property.status}
                    sx={{
                      backgroundColor: property.status === 'Available' ? '#008080' : '#FFA500',
                      color: 'white'
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Links / External Images */}
            {(property.igGuideUrl || (property.externalImageUrls && property.externalImageUrls.length > 0)) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Links</Typography>
                  <Stack spacing={2}>
                    {property.igGuideUrl && (
                      <Button
                        variant="outlined"
                        fullWidth
                        component="a"
                        href={normalizeUrl(property.igGuideUrl)}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          borderColor: '#008080',
                          color: '#008080',
                          '&:hover': { borderColor: '#005252', backgroundColor: 'rgba(0, 128, 128, 0.04)' }
                        }}
                      >
                        Open iGUIDE / Virtual Tour
                      </Button>
                    )}

                    {property.externalImageUrls && property.externalImageUrls.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Property Photos
                        </Typography>
                        <Stack spacing={1}>
                          {property.externalImageUrls.map((url, idx) => (
                            <Button
                              key={`${url}-${idx}`}
                              variant="outlined"
                              fullWidth
                              component="a"
                              href={normalizeUrl(url)}
                              target="_blank"
                              rel="noreferrer"
                              startIcon={<Photo />}
                              sx={{
                                borderColor: '#008080',
                                color: '#008080',
                                justifyContent: 'center',
                                textTransform: 'none',
                                '&:hover': { borderColor: '#005252', backgroundColor: 'rgba(0, 128, 128, 0.04)' }
                              }}
                            >
                              {property.externalImageUrls.length === 1 ? 'View Photos' : `View Photos ${idx + 1}`}
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Property Disclosures */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Property Disclosures</Typography>
                {property.disclosures && property.disclosures.length > 0 ? (
                  <Stack spacing={1}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (property.disclosures[0]?.downloadURL) {
                          window.open(property.disclosures[0].downloadURL, '_blank');
                        }
                      }}
                      sx={{
                        borderColor: '#FFA500',
                        color: '#000000',
                        backgroundColor: '#FFA500',
                        '&:hover': {
                          borderColor: '#CC8400',
                          backgroundColor: '#CC8400',
                          color: '#000000'
                        }
                      }}
                    >
                      View Disclosures ({property.disclosures.length} file{property.disclosures.length > 1 ? 's' : ''})
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        property.disclosures.forEach((disclosure, index) => {
                          if (disclosure.downloadURL) {
                            const link = document.createElement('a');
                            link.href = disclosure.downloadURL;
                            link.download = disclosure.name || `disclosure_${index + 1}`;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        });
                      }}
                      sx={{
                        borderColor: '#FFA500',
                        color: '#FFA500',
                        '&:hover': {
                          borderColor: '#CC8400',
                          backgroundColor: 'rgba(255, 165, 0, 0.04)'
                        }
                      }}
                    >
                      Download All Disclosures
                    </Button>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                      Files:
                    </Typography>
                    {property.disclosures.map((disclosure, index) => (
                      <Button
                        key={index}
                        variant="text"
                        size="small"
                        onClick={() => window.open(disclosure.downloadURL, '_blank')}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          color: '#666',
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 165, 0, 0.04)',
                            color: '#000'
                          }
                        }}
                      >
                        📄 {disclosure.name || `Disclosure ${index + 1}`}
                        {disclosure.size && (
                          <span style={{ marginLeft: '8px', color: '#999' }}>
                            ({(disclosure.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        )}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No disclosures available
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                {vendor ? (
                  <>
                    {(vendor.phone || vendor.email || vendor.location) ? (
                      <Stack spacing={1}>
                        {vendor.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Phone sx={{ mt: '2px', fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              <Link
                                href={`tel:${String(vendor.phone).replace(/\s+/g, '')}`}
                                underline="hover"
                                sx={{ color: 'inherit' }}
                              >
                                {vendor.phone}
                              </Link>
                            </Typography>
                          </Box>
                        )}
                        {vendor.email && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Email sx={{ mt: '2px', fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              <Link
                                href={`mailto:${vendor.email}`}
                                underline="hover"
                                sx={{ color: 'inherit', wordBreak: 'break-word' }}
                              >
                                {vendor.email}
                              </Link>
                            </Typography>
                          </Box>
                        )}
                        {vendor.location && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationOn sx={{ mt: '2px', fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {vendor.location}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Vendor contact details not available.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Vendor information not available.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Vendor Profile */}
            {vendor && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Vendor Profile</Typography>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar
                      src={vendor.profilePhoto}
                      sx={{ width: 48, height: 48 }}
                    >
                      {vendor.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">{vendor.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {vendor.title} at {vendor.company}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(`/vendor/${vendor.id}`)}
                    sx={{ borderColor: '#008080', color: '#008080' }}
                  >
                    View Vendor Profile
                  </Button>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={shareToast.open}
        autoHideDuration={2500}
        onClose={() => setShareToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShareToast({ open: false, message: '' })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {shareToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
